const LOCAL_STORAGE_WISHLIST_KEY = "shopify-wishlist";
const LOCAL_STORAGE_DELIMITER = ",";

function getWishlist() {
  const wishlist = localStorage.getItem(LOCAL_STORAGE_WISHLIST_KEY) || "";
  if (!wishlist) return [];
  const rawList = wishlist.split(LOCAL_STORAGE_DELIMITER);
  // Clean up any items that have query parameters or full URLs
  const cleanedList = rawList.map(item => {
    const noQuery = item.split('?')[0];
    const noTrailingSlash = noQuery.replace(/\/+$/, '');
    return noTrailingSlash.split('/').pop().trim();
  }).filter(Boolean);
  const uniqueList = [...new Set(cleanedList)];
  
  // If the list changed during sanitization, update localStorage
  if (uniqueList.length !== rawList.length || uniqueList.some((val, idx) => val !== rawList[idx])) {
    if (uniqueList.length) {
      localStorage.setItem(LOCAL_STORAGE_WISHLIST_KEY, uniqueList.join(LOCAL_STORAGE_DELIMITER));
    } else {
      localStorage.removeItem(LOCAL_STORAGE_WISHLIST_KEY);
    }
  }
  return uniqueList;
}

function setWishlist(array) {
  const wishlist = array.join(LOCAL_STORAGE_DELIMITER);
  if (array.length) {
    localStorage.setItem(LOCAL_STORAGE_WISHLIST_KEY, wishlist);
  } else {
    localStorage.removeItem(LOCAL_STORAGE_WISHLIST_KEY);
  }
  return wishlist;
}

function updateWishlist(handle) {
  const wishlist = getWishlist();
  const indexInWishlist = wishlist.indexOf(handle);

  if (indexInWishlist === -1) {
    wishlist.push(handle);
  } else {
    wishlist.splice(indexInWishlist, 1);
  }

  setWishlist(wishlist);
  updateGlobalWishlistCounter();
}

function wishlistContains(handle) {
  return getWishlist().includes(handle);
}

function updateGlobalWishlistCounter() {
  const wishlist_item_length = getWishlist().length;
  const WISHLIST_ITEM_COUNT = document.querySelectorAll(".wishlist__count");
  WISHLIST_ITEM_COUNT.forEach((singleItem) => {
    singleItem.innerText = wishlist_item_length;
  });
}

class WishlistProduct extends HTMLElement {
  constructor() {
    super();
    this.onAddWishlist = this.onAddWishlist.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.onAddWishlist);
    // Wait a frame to ensure children elements (like button) are parsed and accessible
    setTimeout(() => {
      this.onAddActiveClass();
      updateGlobalWishlistCounter();
    }, 0);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onAddWishlist);
  }

  onAddActiveClass() {
    const wishlistAddedButton = this.querySelector("button");
    if (wishlistAddedButton != null) {
      const productHandle = wishlistAddedButton.dataset.productHandle || false;
      if (!productHandle) {
        console.error(
          "[Wishlist] Missing `data-product-handle` attribute. Failed to update the wishlist."
        );
        return;
      }
      if (wishlistContains(productHandle)) {
        wishlistAddedButton.classList.add("active");
      } else {
        wishlistAddedButton.classList.remove("active");
      }
    }
  }

  onAddWishlist(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const wishlistItem = this.querySelector("button");
    if (!wishlistItem) return;

    const productHandle = wishlistItem.dataset.productHandle || false;
    if (!productHandle) {
      console.error(
        "[Wishlist] Missing `data-product-handle` attribute. Failed to update the wishlist."
      );
      return;
    }

    updateWishlist(productHandle);

    const grid = document.querySelector("[grid-wishlist]") || false;

    if (grid) {
      const card = this.closest(".product__card") || this.closest(".pcard");
      if (card) {
        card.remove();
      }
      const wishlist_item_length = getWishlist().length;
      if (wishlist_item_length === 0) {
        const wihslitExistBtn = document.querySelector(".wishlist-page");
        if (wihslitExistBtn) {
          wihslitExistBtn.classList.remove("wishlist_exists");
        }
        grid.parentElement.classList.remove("wishlist_exists");
      }
    } else {
      wishlistItem.classList.add("loading", "adding");
      setTimeout(() => {
        wishlistItem.classList.remove("loading");
        wishlistItem.classList.toggle("active");
      }, 500);

      setTimeout(() => {
        const addingElement = document.querySelector(".adding");
        if (addingElement) {
          addingElement.classList.remove("adding");
        }
      }, 1000);
    }
  }
}

// Define the custom element if it hasn't been registered yet
if (!customElements.get("wishlist-item")) {
  customElements.define("wishlist-item", WishlistProduct);
}

// Initialize on page load (handles race condition if page is already loaded)
function initWishlist() {
  updateGlobalWishlistCounter();

  const grid = document.querySelector("[grid-wishlist]") || false;
  if (grid) {
    setupGrid(grid);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initWishlist);
} else {
  initWishlist();
}

function setupGrid(grid) {
  const wishlist = getWishlist();
  const wihslitExistBtn = document.querySelector(".wishlist-page");

  if (wihslitExistBtn) {
    if (wishlist.length > 0) {
      wihslitExistBtn.classList.add("wishlist_exists");
    } else {
      wihslitExistBtn.classList.remove("wishlist_exists");
    }
  }

  if (wishlist.length === 0) {
    grid.innerHTML = "";
    return;
  }

  const requests = wishlist.map((handle) => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const productTileTemplateUrl = `${root.replace(/\/+$/, '')}/products/${handle}?view=wishlist`;
    return fetch(productTileTemplateUrl)
      .then((res) => {
        if (res.status === 200) {
          return res.text().then((html) => {
            if (html.includes('class="pcard"') || html.includes("class='pcard'")) {
              return html;
            }
            throw new Error("Invalid alternate template response");
          });
        }
        throw new Error("Template fetch failed");
      })
      .catch((err) => {
        console.warn(`[Wishlist] Custom template failed for ${handle}, falling back to JSON API:`, err);
        const jsonUrl = `${root.replace(/\/+$/, '')}/products/${handle}.js`;
        return fetch(jsonUrl)
          .then((res) => res.json())
          .then((p) => {
            return renderProductCardJS(p);
          })
          .catch((jsonErr) => {
            console.error(`[Wishlist] Failed to fetch product ${handle} JSON:`, jsonErr);
            return "";
          });
      });
  });

  Promise.all(requests).then((responses) => {
    const wishlistProductCards = responses.filter(Boolean).join("");
    if (wishlistProductCards.trim() === "") {
      grid.innerHTML = "";
      if (wihslitExistBtn) {
        wihslitExistBtn.classList.remove("wishlist_exists");
      }
    } else {
      grid.innerHTML = wishlistProductCards;
    }
  });
};

function renderProductCardJS(p) {
  const variant = p.variants && p.variants[0] ? p.variants[0] : {};
  const price = p.price;
  const compareAtPrice = p.compare_at_price;
  const available = p.available;
  const discount = (compareAtPrice > price) ? Math.round(((compareAtPrice - price) * 100) / compareAtPrice) : 0;
  
  // Scarcity stock indicators
  const finalStock = (p.id % 10) + 3;
  const progressPercent = Math.round((finalStock * 100) / 15);
  const soldCount = (p.id % 30) + 20;
  const imageUrl = p.featured_image || '';
  const priceFormatted = "Rs. " + (price / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const comparePriceFormatted = compareAtPrice ? "Rs. " + (compareAtPrice / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "";
  
  const badgeHTML = !available ? `<span class="soldout">Sold Out</span>` : (discount > 0 ? `<span class="pbadge">Sale</span>` : '');
  
  const buttonHTML = available 
    ? `<product-form>
         <form method="post" action="/cart/add" class="product-form-card" enctype="multipart/form-data">
           <input type="hidden" name="id" value="${variant.id || ''}">
           <button type="submit" name="add" class="addbtn">+ Add</button>
         </form>
       </product-form>`
    : `<button class="addbtn disabled" disabled>Sold Out</button>`;

  const mrpHTML = (compareAtPrice > price) 
    ? `<s>${comparePriceFormatted}</s> <b>${discount}% OFF</b>` 
    : '';

  const stockHTML = available
    ? `<div class="urg low"><span class="dot"></span>Only ${finalStock} left</div>
       <div class="stockbar"><i style="width:${progressPercent}%"></i></div>`
    : `<div class="urg low"><span class="dot" style="background:#d9534f"></span>Out of stock</div>
       <div class="stockbar"><i style="width:0%; background:#d9534f"></i></div>`;

  const safeName = p.title.replace(/"/g, '&quot;');
  
  return `
    <div class="col mb-30">
      <div class="pcard" data-product-id="${p.id}" data-price="${price / 100}" data-stock="${available ? 'in' : 'out'}" data-name="${safeName}">
        <div class="pimg">
          ${badgeHTML}
          <wishlist-item>
            <button wishlist-button class="wishlist__button pwish active" type="button" data-product-handle="${p.handle}">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path fill="currentColor" d="M462.3 62.7c-54.5-46.4-136-38.7-186.6 13.5L256 96.6l-19.7-20.3C195.5 34.1 113.2 8.7 49.7 62.7c-62.8 53.6-66.1 149.8-9.9 207.8l193.5 199.8c6.2 6.4 14.4 9.7 22.6 9.7 8.2 0 16.4-3.2 22.6-9.7L472 270.5c56.4-58 53.1-154.2-9.7-207.8z" />
              </svg>
            </button>
          </wishlist-item>
          <button class="qv-eye" onclick="event.preventDefault();event.stopPropagation();qview(this)">
            <svg viewBox="0 0 24 24"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/></svg>
            Quick view
          </button>
          <a href="${p.url}">
            <img src="${imageUrl}" alt="${safeName}" loading="lazy">
          </a>
        </div>
        <div class="pmeta">
          <div class="prate">
            <span class="stars">★★★★★</span>
            <small>4.9 · 12</small>
          </div>
          <h3 class="pname">
            <a href="${p.url}">${p.title}</a>
          </h3>
          ${stockHTML}
          <div class="urg"><span class="dot" style="background:#a0a0a0"></span>${soldCount} sold this week</div>
          <div class="prow">
            <div>
              <div class="pprice">${priceFormatted}</div>
              <div class="pmrp">${mrpHTML}</div>
            </div>
            ${buttonHTML}
          </div>
        </div>
      </div>
    </div>
  `;
}
